import { memo, useState, useCallback, useEffect, useMemo } from "react";
import Alert from '@mui/material/Alert';

import OffcanvasTemplate from "../../../common/offcanvasTemplate";
import { fetchFtmRequest } from "../service";
import { theme } from "../../../theme";
import BtnSpinner from "../../../common/btnSpinner";
import OptionMenu from "../components/OptionMenu";
import DivSpinner from "../../../common/divSpinner";
import JobCost from "../components/JobCost";
import { ShowMoveDetails } from "../components/ShowMoveDetails";
import FullRequestDetails from "../components/FullRequestDetails";

/* -------------------- Static Components -------------------- */

const Body = memo(({ isDirectFetch }) => {
    if (isDirectFetch) return <DivSpinner />;

    return (
        <div className="ftm-request-notification-body mt-6 text-center">
            <h3>REQUEST</h3>
            <img
                src="/assets/gif/bell.gif"
                className="ftm-request-notification-body-icon"
                alt="bellIcon"
            />
            <p className="r3 px-md-5 px-sm-1">
                Please find the requested extra time or materials by Tradesperson
            </p>
        </div>
    );
});

const Footer = memo((props) => {
    const {
        loading,
        showBell,
        isDirectFetch,
        handleView,
        closeView,
        jobId,
        requestId,
        isShowUndecidedBtn,
        reloadPage
    } = props;

    return (
        <div className="container">
            <div className="row mt-2">
                <div className="col text-center">
                    <div className="d-grid gap-2">
                        {showBell ? (
                            !isDirectFetch && (
                                <button
                                    className={`btn btn-icon btn-3 btn-${theme.redirect_btn} btn-sm`}
                                    disabled={loading}
                                    onClick={handleView}
                                >
                                    {loading ? (
                                        <BtnSpinner />
                                    ) : (
                                        <>
                                            <span className="btn-inner--icon">
                                                <i
                                                    className="material-symbols-rounded"
                                                    style={{ fontSize: "20px" }}
                                                >
                                                    visibility
                                                </i>
                                            </span>
                                            <span className="btn-inner--text">
                                                VIEW
                                            </span>
                                        </>
                                    )}
                                </button>
                            )
                        ) : (
                            <OptionMenu
                                job_id={jobId}
                                request_id={requestId}
                                closeView={closeView}
                                isShowUndecidedBtn={isShowUndecidedBtn}
                                reloadPage={reloadPage}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
});

/* -------------------- Main Component -------------------- */

const FtmRequestAlert = ({
    show,
    request_data,
    handleClose,
    isDirectFetch = false,
    isShowUndecidedBtn = true,
    reloadPage = () => {},
    isAlert=false
}) => {
    const [loading, setLoading] = useState(false);
    const [request, setRequest] = useState(null);
    const [showBell, setShowBell] = useState(true);
    const [showMoveDetails, setShowMoveDetails] = useState(false);

    /* -------- Fetch Request -------- */
    const fetchRequest = useCallback(async () => {
        if (!request_data?.request_id) return;

        setLoading(true);
        try {
            const response = await fetchFtmRequest(request_data.request_id);
            if (response?.status === "Success") {
                setRequest(response.data);
                setShowBell(false);
            }
        } catch (error) {
            console.error("Error fetching FTM request:", error);
        } finally {
            setLoading(false);
        }
    }, [request_data?.request_id]);

    useEffect(() => {
        if (!show || !isDirectFetch) return;
        fetchRequest();
    }, [show, isDirectFetch, fetchRequest]);

    /* -------- Handlers -------- */
    const closeView = useCallback(() => {
        setLoading(false);
        setRequest(null);
        setShowBell(true);
        handleClose();
    }, [handleClose]);

    const handleMoveDetails = useCallback(() => {
        setShowMoveDetails(prev => !prev);
    }, []);

    const handleView = useCallback(() => {
        fetchRequest();
    }, [fetchRequest]);

    /* -------- Memoized JobCost Data -------- */
    const jobCostRequest = useMemo(() => {
        if (!request) return null;

        const {
            ftm_request_is_undecided,
            ftm_request_is_accept,
            ftm_request_images,
            ftm_request_id,
            ftm_request_description,
            request_materials
        } = request;

        return {
            ftm_request_is_undecided,
            ftm_request_is_accept,
            ftm_request_images,
            ftm_request_id,
            ftm_request_description,
            request_materials
        };
    }, [request]);

    const shouldHideStatus = useMemo(() => {
        if (!jobCostRequest) return false;

        return (
            jobCostRequest.ftm_request_is_accept === 1 ||
            jobCostRequest.ftm_request_is_accept === -1 ||
            jobCostRequest.ftm_request_is_undecided === 1
        );
    }, [jobCostRequest]);

    /* -------- Footer Props -------- */
    const footerProps = {
        loading,
        showBell,
        isDirectFetch,
        handleView,
        closeView,
        jobId: request_data?.job_id,
        requestId: request_data?.request_id,
        isShowUndecidedBtn,
        reloadPage
    };

    return (
        <OffcanvasTemplate
            open={show}
            handleClose={closeView}
            placement="end"
            title={showBell ? null : "Tradespersons Requests"}
            isShowCloseButton={!showBell}
            body={
                showBell ? (
                    <Body isDirectFetch={isDirectFetch} />
                ) : (
                    <>
                        {shouldHideStatus && (
                            <Alert
                                severity={
                                    jobCostRequest.ftm_request_is_accept === 1 ? "success" : 
                                    jobCostRequest.ftm_request_is_undecided === 1 ? "warning" : "error"
                                }
                            >
                                {
                                    jobCostRequest.ftm_request_is_accept === 1 ? "Already accepted this request." : 
                                    jobCostRequest.ftm_request_is_undecided === 1 ? "This request is undecided." : "Already rejected this request."
                                }
                            </Alert>
                        )}

                        {showMoveDetails ? (
                            <FullRequestDetails request={request} />
                        ) : (
                            <JobCost request={jobCostRequest} isAlert={isAlert}/>
                        )}

                        <ShowMoveDetails
                            showMoveDetails={showMoveDetails}
                            action={handleMoveDetails}
                        />

                        {!shouldHideStatus && (
                            <Footer {...footerProps} />
                        )}
                    </>
                )
            }
            footer={
                showBell ? <Footer {...footerProps} /> : null
            }
            backdrop={isDirectFetch}
        />
    );
};

export default memo(FtmRequestAlert);